import React from 'react';
import { View, TouchableOpacity } from 'react-native';
import styled from '@emotion/native';
import { BodyText } from '@/shared/components/StyledText';
import { white, sand, green, placeholder } from '@/shared/styles/colors';
import {
  getNotificationTitle,
  getNotificationDescription,
  formatTimestamp,
} from './helpers';
import { Notification } from '@/shared/types/generated';

interface NotificationItemProps {
  notification: Notification;
  onPress?: () => void;
  isAlternate?: boolean;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onPress,
  isAlternate = false,
}) => {
  const handlePress = () => {
    if (onPress) {
      onPress();
    }
  };

  return (
    <Container onPress={handlePress} isAlternate={isAlternate}>
      <ContentRow>
        <UnreadIndicator isRead={notification.isRead} />
        <TextContent>
          <TitleText isRead={notification.isRead}>
            {getNotificationTitle(notification.type)}
          </TitleText>
          <DescriptionText>
            {getNotificationDescription(
              notification.type,
              notification.variables,
            )}
          </DescriptionText>
        </TextContent>
        <TimestampText>
          {formatTimestamp(new Date(notification.createdAt))}
        </TimestampText>
      </ContentRow>
    </Container>
  );
};

const Container = styled(TouchableOpacity)<{ isAlternate?: boolean }>(
  ({ isAlternate }) => ({
    backgroundColor: isAlternate ? sand : white,
    padding: 16,
    width: '100%',
  }),
);

const ContentRow = styled(View)({
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
});

const UnreadIndicator = styled(View)<{ isRead: boolean }>(({ isRead }) => ({
  width: 8,
  height: 8,
  borderRadius: 4,
  backgroundColor: isRead ? 'transparent' : green,
  marginRight: 12,
  flexShrink: 0,
  alignSelf: 'center',
}));

const TextContent = styled(View)({
  flex: 1,
  marginRight: 12,
});

const TitleText = styled(BodyText)<{ isRead: boolean }>(({ isRead }) => ({
  color: green,
  fontSize: 16,
  fontFamily: 'Helvetica',
  fontWeight: isRead ? '400' : '700',
  letterSpacing: -0.16,
  marginBottom: 4,
}));

const DescriptionText = styled(BodyText)({
  color: green,
  fontSize: 14,
  fontFamily: 'Helvetica',
  fontWeight: '400',
  lineHeight: 18.9,
});

const TimestampText = styled(BodyText)({
  color: placeholder,
  fontSize: 13,
  fontFamily: 'Helvetica',
  fontWeight: '400',
  letterSpacing: 0.26,
  lineHeight: 17.55,
  flexShrink: 0,
  textAlign: 'right',
  marginTop: 2,
  marginRight: 6,
});

export default NotificationItem;
